import { Component, Input, OnInit } from '@angular/core';
import { ReportsService } from '../reports-service';
import {
  CockpitScoreOption,
  ENTITY_EXTRA_REPORT_TYPES,
  PORTFOLIO_EXTRA_REPORT_TYPES,
  QUERY_REPORT_TYPES,
  ReportSubscription,
  scoreToReportType,
} from 'src/app/shared/utils/reports';
import getScoreTypeDetails from 'src/app/shared/utils/score-type-details';
import {
  ReportsSetupFormService,
  ReportFormData,
} from '../reports-setup-form-service';

import { EntityInfoService } from 'src/app/shared/services/metadata-api/entity-info.service';
import { QueryApiService } from 'src/app/shared/services/customer-api/query-api.service';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';

@Component({
  selector: 'app-reports-settings',
  templateUrl: './reports-settings.component.html',
  styleUrls: ['./reports-settings.component.scss'],
})
export class ReportsSettingsComponent implements OnInit {
  @Input() scoreOptions: CockpitScoreOption[] = [];
  subscriptions: ReportSubscription[] = [];

  loadingReports = false;
  currentReport: ReportFormData;
  private reportListSubject = new BehaviorSubject<ReportFormData[]>([]);
  reportList$ = this.reportListSubject.asObservable();

  constructor(
    private reportsService: ReportsService,
    private reportFormService: ReportsSetupFormService,
    private reportService: ReportsService,
    private entityInfoService: EntityInfoService,

    private queryApi: QueryApiService,
    public snackBar: MatSnackBar,
  ) {
    this.currentReport = this.reportFormService.getCurrentReport();
  }

  ngOnInit() {
    this.loadingReports = true;

    this.reportsService.subscriptions$.subscribe((subscriptions) => {
      if (subscriptions && subscriptions.length > 0) {
        this.subscriptions = subscriptions;
        this.generateReportList();
      }
    });

    this.reportList$.subscribe((reportList) => {
      if (reportList.length > 0) {
        this.reportFormService.setupReportFromSubscription(reportList[0]);
      }
    });

    this.loadSubscriptions();

    this.reportFormService.currentReport$.subscribe((report) => {
      this.currentReport = report;
    });
  }

  selectReport(report: ReportFormData) {
    this.reportFormService.setupReportFromSubscription(report);
  }

  loadSubscriptions() {
    this.reportsService.getAllSubscriptions().subscribe({
      next: (data) => {
        if (data && data.length > 0) {
          this.subscriptions = data;

          this.generateReportList();
        }
      },
      error: (error) => console.error('Error fetching subscriptions', error),
      complete: () => {
        this.loadingReports = false;
      },
    });
  }

  createReport() {
    this.reportFormService.createNewReport();
  }

  reportStatusUpdate(value: boolean, report: ReportFormData) {
    if (!report.id) {
      console.warn('Cannot toggle status: subscription has no id');
      return;
    }
    const reportStatusMesssage = value ? 'activated' : 'deactivated';

    this.reportService
      .updateSubscription(report.id, { active: value })
      .subscribe({
        next: () => {
          const currentList = this.reportListSubject.getValue();
          const updatedList = currentList.map((r) =>
            r.id === report.id ? { ...r, active: value } : r,
          );
          this.reportListSubject.next(updatedList);
          this.reportFormService.setupReportFromSubscription(report);
        },
        complete: () => {
          this.snackBar.open(
            'Report Subscription ' + reportStatusMesssage + '.',
            'OK',
            {
              duration: 5000,
            },
          );
        },
        error: (error) => {
          console.error('Error updating subscription status', error);
          this.snackBar.open(
            'Error updating subscription status. Please try again.',
            'OK',
            {
              duration: 5000,
            },
          );
        },
      });
  }

  private generateReportList() {
    if (!this.subscriptions || this.subscriptions.length === 0) {
      console.warn('No subscriptions available to generate report list.');
      return;
    }

    const allQueries$ = firstValueFrom(this.queryApi.getQuery());

    const sortedSubscriptions = [...this.subscriptions].sort((a, b) => {
      const aTime = a.createdOn ? Date.parse(a.createdOn) : 0;
      const bTime = b.createdOn ? Date.parse(b.createdOn) : 0;
      return bTime - aTime;
    });

    const reportList: ReportFormData[] = new Array(sortedSubscriptions.length);

    const entityRequests = sortedSubscriptions.map((subscription, index) => {
      const payload = subscription.payload;

      if (payload.kind === 'company') {
        const companyKey = `company:${payload.companyId}`;
        return firstValueFrom(
          this.entityInfoService.getEntityName([companyKey]),
        ).then((companyName) => {
          reportList[index] = {
            id: subscription.id,
            kind: 'entity',
            reportObject: companyKey,
            entityName: companyName,
            active: subscription.active,
            schedule: subscription.schedule,
            reportType: subscription.reportType,
            recipients: subscription.recipients,
            isNew: false,
            language: payload.language,
          };
        });
      }

      if (payload.kind === 'portfolio') {
        reportList[index] = {
          id: subscription.id,
          kind: 'portfolio',
          reportObject: payload.portfolioId,
          portfolioName: payload.portfolioName,
          active: subscription.active,
          schedule: subscription.schedule,
          reportType: subscription.reportType,
          recipients: subscription.recipients,
          isNew: false,
          language: payload.language,
        };
        return Promise.resolve();
      }

      if (payload.kind === 'query') {
        return allQueries$.then((queries) => {
          const match = (queries || []).find(
            (q: { uid: string; name: string }) => q.name === payload.queryName,
          );
          reportList[index] = {
            id: subscription.id,
            kind: 'query',
            reportObject: match?.uid,
            queryName: payload.queryName,
            active: subscription.active,
            schedule: subscription.schedule,
            reportType: subscription.reportType,
            recipients: subscription.recipients,
            isNew: false,
            language: payload.language,
          };
        });
      }

      return Promise.resolve();
    });

    Promise.all(entityRequests).then(() => {
      this.reportListSubject.next(reportList.filter(Boolean));
    });
  }

  private readonly reportTypeExtras: ReadonlyArray<{
    value: string;
    label: string;
  }> = [
    ...ENTITY_EXTRA_REPORT_TYPES,
    ...PORTFOLIO_EXTRA_REPORT_TYPES,
    ...QUERY_REPORT_TYPES,
  ];

  getReportTypeLabel(reportType: string): string {
    const matched = this.scoreOptions.find(
      (option) => scoreToReportType(option.value) === reportType,
    );
    if (matched) {
      const reportLabel = getScoreTypeDetails(matched.value).reportLabel;
      return reportLabel || matched.label;
    }
    return (
      this.reportTypeExtras.find((option) => option.value === reportType)
        ?.label ?? ''
    );
  }

  setReportTitle(report): string {
    let name: string | undefined;
    switch (report.kind) {
      case 'portfolio':
        name = report.portfolioName;
        break;
      case 'entity':
        name = report.entityName;
        break;
      case 'query':
        name = report.queryName;
        break;
      default:
        return '';
    }

    const label = this.getReportTypeLabel(report.reportType);
    return [name, label].filter(Boolean).join(' · ');
  }
}
